import Phaser from 'phaser';
import { SCENE_KEYS, GAME_CONFIG } from '@/config/constants';
import { registerPlayer } from '@/utils/SupabaseClient';
import { AudioManager } from '@/utils/AudioManager';
import { transitionTo, fadeIn } from '@/utils/UIAnimations';
import { COLORS, COLORS_HEX, FONTS, RADIUS, TEXT_PRESET } from '@/ui/theme';
import { GamepadNav } from '@/utils/GamepadNav';
import { createPillButton } from '@/ui/PillButton';
import { drawSelectionHalo } from '@/ui/SelectionHalo';

interface KeyCell {
  x: number; y: number; w: number; h: number;
  press: () => void;
  setFocus: (on: boolean) => void;
  // Set only for pill CTA buttons: their real (elliptical) cap radii, so the
  // focus ring offsets parallel to the stretched-SVG edge instead of bowing out.
  capRx?: number;
  capRy?: number;
  // SPACE / DEL get a focus frame whose corner radius matches the key's own
  // 8px rounded corners, instead of the larger 16px radius the letter keys use.
  matchKeyRadius?: boolean;
}

/**
 * Name Entry Scene — Code-drawn glassmorphism UI matching MenuScene style
 */
export class NameEntryScene extends Phaser.Scene {
  private playerName: string = '';
  private nameText?: Phaser.GameObjects.Text;
  private cursorChar?: Phaser.GameObjects.Text;
  private selectedStage: number = 1;
  private maxNameLength: number = 12;
  private minNameLength: number = 2;
  private statusText?: Phaser.GameObjects.Text;
  private nav?: GamepadNav;
  private rows: KeyCell[][] = [];
  private focusRow = -1;
  private focusCol = -1;
  private focusHalo?: Phaser.GameObjects.Graphics;

  constructor() {
    super({ key: SCENE_KEYS.NAME_ENTRY });
  }

  init(data?: { stage?: number }): void {
    this.selectedStage = data?.stage || 1;
    this.playerName = '';
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const cx = WIDTH / 2;
    const cy = HEIGHT / 2;

    this.rows = [];
    this.focusRow = -1;
    this.focusCol = -1;

    (this as any)._transitioning = false;
    fadeIn(this, 400);

    const videoKey = 'bg_video_world1_video';
    if (this.cache.video.has(videoKey)) {
      const videoBg = this.add.video(cx, cy, videoKey);
      videoBg.setDepth(0).setScale(1).setVolume(0).play(true).setAlpha(0.7);
    }

    const bg = this.add.graphics().setDepth(1);
    bg.fillGradientStyle(COLORS.midnightDepth, COLORS.midnightDepth, COLORS.deepOcean, COLORS.deepOcean, 0.9, 0.9, 0.98, 0.98);
    bg.fillRect(0, 0, WIDTH, HEIGHT);

    const glows = this.add.graphics().setDepth(2);
    glows.fillStyle(COLORS.aqua, 0.1);
    glows.fillCircle(cx, cy - 120, 260);
    glows.fillStyle(COLORS.sapphire, 0.14);
    glows.fillCircle(cx - 360, cy + 80, 220);
    glows.fillCircle(cx + 360, cy + 60, 220);

    const vig = this.add.graphics().setDepth(3);
    vig.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0.78, 0.78, 0, 0).fillRect(0, 0, WIDTH, 150);
    vig.fillGradientStyle(0x000000, 0x000000, 0x000000, 0x000000, 0, 0, 0.82, 0.82).fillRect(0, HEIGHT - 150, WIDTH, 150);

    // === CENTERED PANEL (rounded) ===
    const pw = 840, ph = 760, pRadius = RADIUS.xl;
    const plx = cx - pw / 2;
    const pty = (HEIGHT - ph) / 2 - 30;

    const panel = this.add.graphics().setDepth(4);
    panel.fillStyle(COLORS.aqua, 0.08);
    panel.fillRoundedRect(plx - 12, pty - 10, pw + 24, ph + 24, pRadius + 10);
    panel.fillStyle(COLORS.midnightDepth, 0.5);
    panel.fillRoundedRect(plx, pty + 8, pw, ph, pRadius);
    panel.fillGradientStyle(COLORS.tidewater, COLORS.tidewater, COLORS.abyssBlue, COLORS.abyssBlue, 0.94, 0.94, 0.92, 0.92);
    panel.fillRoundedRect(plx, pty, pw, ph, pRadius);
    panel.fillStyle(COLORS.pearlWhite, 0.06);
    panel.fillRoundedRect(plx + 10, pty + 10, pw - 20, 118, pRadius - 10);
    panel.lineStyle(2, COLORS.aqua, 0.62);
    panel.strokeRoundedRect(plx, pty, pw, ph, pRadius);
    panel.lineStyle(1, COLORS.pearlWhite, 0.22);
    panel.strokeRoundedRect(plx + 4, pty + 4, pw - 8, ph - 8, pRadius - 4);
    panel.fillStyle(COLORS.aquaLight, 0.34);
    panel.fillRoundedRect(plx + 64, pty + 4, pw - 128, 3, 2);

    // === TITLE ===
    this.add.text(cx, pty + 64, 'SET YOUR USERNAME', {
      ...TEXT_PRESET.section,
      fontFamily: FONTS.fancy,
      fontSize: '46px',
      color: COLORS_HEX.goldLight,
    } as Phaser.Types.GameObjects.Text.TextStyle).setOrigin(0.5).setDepth(6);

    // Divider
    const divG = this.add.graphics().setDepth(6);
    const divY = pty + 122;
    divG.lineStyle(1, COLORS.aqua, 0.35);
    divG.lineBetween(cx - 240, divY, cx + 240, divY);
    divG.fillStyle(COLORS.aqua, 0.5);
    divG.fillTriangle(cx - 5, divY, cx, divY - 4, cx + 5, divY);
    divG.fillTriangle(cx - 5, divY, cx, divY + 4, cx + 5, divY);

    this.add.text(cx, pty + 154, 'Leaderboard identity only. Every new run begins from Stage 1.', {
      fontFamily: FONTS.display,
      fontSize: '22px',
      color: COLORS_HEX.textSecondary,
      stroke: COLORS_HEX.midnightDepth,
      strokeThickness: 1,
    }).setOrigin(0.5).setDepth(6);

    // === NAME INPUT FIELD ===
    const inputY = pty + 250;
    const inputW = 620, inputH = 78;
    const inputGfx = this.add.graphics().setDepth(5);
    inputGfx.fillStyle(COLORS.midnightDepth, 0.78);
    inputGfx.fillRoundedRect(cx - inputW / 2, inputY - inputH / 2, inputW, inputH, 10);
    inputGfx.fillStyle(COLORS.pearlWhite, 0.05);
    inputGfx.fillRoundedRect(cx - inputW / 2 + 8, inputY - inputH / 2 + 8, inputW - 16, inputH * 0.34, 8);
    inputGfx.lineStyle(2, COLORS.aqua, 0.52);
    inputGfx.strokeRoundedRect(cx - inputW / 2, inputY - inputH / 2, inputW, inputH, 10);
    inputGfx.lineStyle(1, COLORS.pearlWhite, 0.16);
    inputGfx.strokeRoundedRect(cx - inputW / 2 + 3, inputY - inputH / 2 + 3, inputW - 6, inputH - 6, 8);

    // Name text
    this.nameText = this.add.text(cx, inputY, '', {
      fontFamily: FONTS.display, fontSize: '48px', color: COLORS_HEX.goldLight,
      stroke: COLORS_HEX.midnightDepth, strokeThickness: 4,
    }).setOrigin(0.5).setDepth(7);

    // Blinking cursor
    this.cursorChar = this.add.text(cx + 10, inputY, '|', {
      fontFamily: FONTS.display, fontSize: '48px', color: COLORS_HEX.aqua,
    }).setOrigin(0, 0.5).setDepth(7);
    this.tweens.add({ targets: this.cursorChar, alpha: 0, duration: 500, yoyo: true, repeat: -1 });

    // === VIRTUAL KEYBOARD ===
    const keyboardRows = ['1234567890', 'QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'];
    const keySize = 58;
    const keyGap = 6;
    const kbStartY = pty + 338;

    keyboardRows.forEach((row, ri) => {
      const rowW = row.length * (keySize + keyGap) - keyGap;
      const rowX = cx - rowW / 2 + keySize / 2;
      const cells: KeyCell[] = [];
      for (let i = 0; i < row.length; i++) {
        const char = row[i];
        const kx = rowX + i * (keySize + keyGap);
        const ky = kbStartY + ri * (keySize + keyGap);
        cells.push(this.createKey(kx, ky, keySize, keySize, char, () => this.addChar(char)));
      }
      this.rows.push(cells);
    });

    // SPACE + DEL row
    const bottomRowY = kbStartY + keyboardRows.length * (keySize + keyGap);
    const spaceW = 240, delW = 120, gap = 20;
    const totalW = spaceW + delW + gap;
    const spaceX = cx - totalW / 2 + spaceW / 2;
    const delX = cx + totalW / 2 - delW / 2;

    const spaceCell = this.createKey(spaceX, bottomRowY, spaceW, keySize, 'SPACE', () => this.addChar(' '), true);
    const delCell   = this.createKey(delX,   bottomRowY, delW,   keySize, 'DEL',   () => this.deleteChar(), true);
    this.rows.push([spaceCell, delCell]);

    // === START GAME BUTTON ===
    const goY = bottomRowY + 100;
    const goBtn = createPillButton(this, {
      x: cx, y: goY,
      label: 'BEGIN STAGE 1',
      variant: 'secondary',
      size: 'medium',
      width: 360,
      onClick: () => this.submitName(),
    });
    goBtn.setDepth(5);
    this.rows.push([this.cellFromContainer(goBtn, cx, goY, 360, 72)]);

    // Status text
    this.statusText = this.add.text(cx, goY + 55, '', {
      fontFamily: FONTS.display, fontSize: '24px', color: COLORS_HEX.dangerRed,
      stroke: COLORS_HEX.midnightDepth, strokeThickness: 2,
    }).setOrigin(0.5).setDepth(5);

    // === BACK BUTTON ===
    const backBtn = createPillButton(this, {
      x: cx, y: pty + ph + 55,
      label: 'BACK TO MENU',
      variant: 'secondary',
      size: 'medium',
      width: 380,
      onClick: () => transitionTo(this, SCENE_KEYS.MENU),
    });
    backBtn.setDepth(5);
    this.rows.push([this.cellFromContainer(backBtn, cx, pty + ph + 55, 380, 72)]);

    // Focus halo overlay — nothing pre-selected (focusRow/Col = -1), so no
    // frame appears until the player moves onto a cell with the joystick /
    // arrows / mouse. Physical keyboard typing still works without focus.
    this.focusHalo = this.add.graphics().setDepth(20);

    // Physical keyboard support — letters/digits/space directly type, etc.
    this.input.keyboard?.on('keydown', (event: KeyboardEvent) => {
      const key = event.key.toUpperCase();
      if (key === 'BACKSPACE') this.deleteChar();
      else if (key === 'ENTER') this.submitName();
      else if (key === 'ARROWUP')    { this.moveFocus(-1, 0); event.preventDefault?.(); }
      else if (key === 'ARROWDOWN')  { this.moveFocus( 1, 0); event.preventDefault?.(); }
      else if (key === 'ARROWLEFT')  { this.moveFocus( 0, -1); event.preventDefault?.(); }
      else if (key === 'ARROWRIGHT') { this.moveFocus( 0,  1); event.preventDefault?.(); }
      else if (key.length === 1 && /[A-Z0-9 ]/.test(key)) this.addChar(key);
    });

    // Gamepad: 2D grid nav. A and B both press the focused cell (so either
    // face button acts as "click" on whatever the halo is on). DEL is its
    // own cell in the grid, BACKSPACE on the physical keyboard still erases.
    // Start submits.
    this.nav = new GamepadNav(this, {
      onUp:    () => this.moveFocus(-1, 0),
      onDown:  () => this.moveFocus( 1, 0),
      onLeft:  () => this.moveFocus( 0, -1),
      onRight: () => this.moveFocus( 0,  1),
      onConfirm: () => this.pressFocused(),
      onPause:   () => this.submitName(),
    });
  }

  update(): void {
    this.nav?.update();
  }

  private cellFromContainer(c: Phaser.GameObjects.Container, x: number, y: number, w: number, h: number): KeyCell {
    c.on('pointerover', () => {
      const r = this.findRowOf(c);
      if (r >= 0) this.setFocus(r, 0);
    });
    return {
      x, y, w, h,
      capRx: (c as any).pillCapRx,
      capRy: (c as any).pillCapRy,
      press: () => c.emit('pointerdown'),
      setFocus: () => {},
    };
  }

  // Return row index of a key cell whose press function fires the same container.
  // Walks the rows arrays — since cellFromContainer is the only producer of length-1 rows
  // anchored to a container, identity comparison via x/y works.
  private findRowOf(c: Phaser.GameObjects.Container): number {
    for (let r = this.rows.length - 1; r >= 0; r--) {
      const cell = this.rows[r][0];
      if (cell && cell.x === c.x && cell.y === c.y) return r;
    }
    return -1;
  }

  private setFocus(row: number, col: number): void {
    if (this.rows.length === 0) return;
    const prev = this.rows[this.focusRow]?.[this.focusCol];
    prev?.setFocus(false);
    this.focusRow = Phaser.Math.Clamp(row, 0, this.rows.length - 1);
    this.focusCol = Phaser.Math.Clamp(col, 0, this.rows[this.focusRow].length - 1);
    const cur = this.rows[this.focusRow][this.focusCol];
    cur?.setFocus(true);
    this.drawHalo();
  }

  private moveFocus(dRow: number, dCol: number): void {
    // No focus yet → first direction press lands on (0, 0) and shows halo.
    if (this.focusRow < 0 || this.focusCol < 0) {
      this.setFocus(0, 0);
      return;
    }
    const cur = this.rows[this.focusRow]?.[this.focusCol];
    if (!cur) return;

    if (dRow !== 0) {
      // Snap to the column in the target row whose x is closest to current x.
      const targetRow = Phaser.Math.Clamp(this.focusRow + dRow, 0, this.rows.length - 1);
      if (targetRow === this.focusRow) return;
      let bestCol = 0;
      let bestDx = Infinity;
      for (let i = 0; i < this.rows[targetRow].length; i++) {
        const dx = Math.abs(this.rows[targetRow][i].x - cur.x);
        if (dx < bestDx) { bestDx = dx; bestCol = i; }
      }
      this.setFocus(targetRow, bestCol);
    } else if (dCol !== 0) {
      const len = this.rows[this.focusRow].length;
      this.setFocus(this.focusRow, Phaser.Math.Clamp(this.focusCol + dCol, 0, len - 1));
    }
  }

  private pressFocused(): void {
    this.rows[this.focusRow]?.[this.focusCol]?.press();
  }

  private drawHalo(): void {
    if (!this.focusHalo) return;
    if (this.focusRow < 0 || this.focusCol < 0) { this.focusHalo.clear(); return; }
    const cell = this.rows[this.focusRow]?.[this.focusCol];
    if (!cell) return;

    // Pill CTA buttons (BEGIN / BACK) offset off their real elliptical caps so
    // the ring stays parallel to the stretched-SVG edge.
    if (cell.capRx != null && cell.capRy != null) {
      drawSelectionHalo(this.focusHalo, {
        cx: cell.x, cy: cell.y, w: cell.w, h: cell.h, rx: cell.capRx, ry: cell.capRy,
      }, { gap: 6, inset: 3 });
      return;
    }

    // Keyboard keys get a frame matching the key shape. SPACE / DEL use the
    // key's own 8px corner radius; the letter keys keep their larger radius.
    this.focusHalo.clear();
    const padX = 6, padY = 6;
    const w = cell.w + padX * 2, h = cell.h + padY * 2;
    const x = cell.x - w / 2, y = cell.y - h / 2;
    if (cell.matchKeyRadius) {
      // Parallel-offset the key's 8px rounded corner by the 6px frame gap so the
      // ring hugs the SPACE / DEL button outline exactly (8 + 6 = 14).
      const r = 8 + padX;
      this.focusHalo.lineStyle(3, 0x88EEFF, 0.95);
      this.focusHalo.strokeRoundedRect(x, y, w, h, r);
      this.focusHalo.lineStyle(1, 0xFFFFFF, 0.6);
      this.focusHalo.strokeRoundedRect(x + 3, y + 3, w - 6, h - 6, Math.max(r - 3, 2));
      return;
    }
    const r = w >= h * 1.6 ? h / 2 : Math.min(h / 2, 16);
    this.focusHalo.lineStyle(3, 0x88EEFF, 0.95);
    this.focusHalo.strokeRoundedRect(x, y, w, h, r);
    this.focusHalo.lineStyle(1, 0xFFFFFF, 0.6);
    this.focusHalo.strokeRoundedRect(x + 3, y + 3, w - 6, h - 6, Math.max(r - 3, 2));
  }

  private createKey(x: number, y: number, w: number, h: number, label: string, callback: () => void, matchKeyRadius = false): KeyCell {
    const audio = AudioManager.getInstance();

    // Key background — code-drawn rounded rect
    const keyBg = this.add.graphics().setDepth(4);
    const drawKey = (fillColor: number, fillAlpha: number, borderColor: number, borderAlpha: number) => {
      keyBg.clear();
      keyBg.fillStyle(fillColor, fillAlpha);
      keyBg.fillRoundedRect(x - w / 2, y - h / 2, w, h, 8);
      keyBg.fillStyle(COLORS.pearlWhite, fillAlpha * 0.08);
      keyBg.fillRoundedRect(x - w / 2 + 4, y - h / 2 + 4, w - 8, h * 0.34, 6);
      keyBg.lineStyle(1.5, borderColor, borderAlpha);
      keyBg.strokeRoundedRect(x - w / 2, y - h / 2, w, h, 8);
    };
    drawKey(COLORS.deepOcean, 0.8, COLORS.aqua, 0.26);

    // Key label
    const fontSize = label.length === 1 ? '32px' : '22px';
    const text = this.add.text(x, y, label, {
      fontFamily: FONTS.display, fontSize, color: COLORS_HEX.aquaLight,
      stroke: COLORS_HEX.midnightDepth, strokeThickness: 2,
    }).setOrigin(0.5).setDepth(5);

    // Hit area
    const hitArea = this.add.rectangle(x, y, w, h, 0x000000, 0).setDepth(6);
    hitArea.setInteractive({ useHandCursor: true });

    const hover = () => {
      drawKey(COLORS.tidewater, 0.8, COLORS.aqua, 0.5);
      text.setColor(COLORS_HEX.goldLight);
    };
    const unhover = () => {
      drawKey(COLORS.deepOcean, 0.8, COLORS.aqua, 0.26);
      text.setColor(COLORS_HEX.aquaLight);
    };
    const press = () => {
      audio.playSFX(this, 'sfx_btn_click', { volume: 0.3 });
      drawKey(COLORS.aqua, 0.4, COLORS.aqua, 0.8);
      this.time.delayedCall(100, () => {
        // After flash, restore to focus or rest state
        if (this.rows[this.focusRow]?.[this.focusCol] === cell) hover();
        else unhover();
      });
      callback();
    };

    hitArea.on('pointerover', () => {
      const idx = this.locateCell(cell);
      if (idx) this.setFocus(idx.r, idx.c);
      else hover();
    });
    hitArea.on('pointerout', () => {
      // Don't unhover if still focused via gamepad/keyboard
      if (this.rows[this.focusRow]?.[this.focusCol] !== cell) unhover();
    });
    hitArea.on('pointerdown', press);

    const cell: KeyCell = {
      x, y, w, h,
      press,
      setFocus: (on) => { if (on) hover(); else unhover(); },
      matchKeyRadius,
    };
    return cell;
  }

  /** Find the [row, col] indices for a given KeyCell. Used by mouse hover. */
  private locateCell(cell: KeyCell): { r: number; c: number } | null {
    for (let r = 0; r < this.rows.length; r++) {
      const c = this.rows[r].indexOf(cell);
      if (c >= 0) return { r, c };
    }
    return null;
  }

  private addChar(char: string): void {
    if (this.playerName.length < this.maxNameLength) {
      this.playerName += char;
      this.updateNameDisplay();
    }
  }

  private deleteChar(): void {
    if (this.playerName.length > 0) {
      this.playerName = this.playerName.slice(0, -1);
      this.updateNameDisplay();
    }
  }

  private updateNameDisplay(): void {
    if (this.nameText) this.nameText.setText(this.playerName);
    if (this.cursorChar && this.nameText) {
      this.cursorChar.setX(this.nameText.x + this.nameText.width / 2 + 5);
    }
    if (this.statusText) this.statusText.setText('');
  }

  private async submitName(): Promise<void> {
    const trimmedName = this.playerName.trim();
    if (trimmedName.length < this.minNameLength) {
      if (this.statusText) this.statusText.setText(`Name must be at least ${this.minNameLength} characters!`);
      return;
    }

    if (this.statusText) {
      this.statusText.setColor(COLORS_HEX.aqua);
      this.statusText.setText('Registering...');
    }

    const player = await registerPlayer(trimmedName);
    if (!player) {
      if (this.statusText) {
        this.statusText.setColor(COLORS_HEX.dangerRed);
        this.statusText.setText('Failed to register. Starting anyway...');
      }
      this.time.delayedCall(1000, () => this.startGameWithName(trimmedName, null));
      return;
    }
    this.startGameWithName(trimmedName, player.id);
  }

  private startGameWithName(name: string, playerId: string | null): void {
    this.registry.set('playerName', name);
    this.registry.set('playerId', playerId);
    // New name = new game → cue the mechanics tutorial card to fire once
    // before stage 1's educational intro. Cleared by GameScene on first show.
    this.registry.set('mechanicsTutorialPending', true);
    this.scene.start(SCENE_KEYS.GAME, { stage: this.selectedStage, score: 0 });
  }
}
